import React from 'react';

/**
 * Hook customizado para funcionalidade de pesquisa
 * @param {Array} items - Array de itens para pesquisar
 * @param {Array} searchFields - Campos nos quais pesquisar (ex: ['user_name', 'carrier_name'])
 */
export const useSearch = (items = [], searchFields = []) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
  const searchInputRef = React.useRef(null);

  // Debounce do termo de pesquisa
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 150);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtrar itens baseado no termo de pesquisa com debounce
  const filteredItems = React.useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return items;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    return items.filter(item =>
      searchFields.some(field =>
        item[field]?.toLowerCase().includes(searchLower)
      )
    );
  }, [items, debouncedSearchTerm, searchFields]);

  // Capturar Cmd+F/Ctrl+F para focar no campo de pesquisa
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Verificar se é Cmd+F (Mac) ou Ctrl+F (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
        event.preventDefault(); // Prevenir a pesquisa padrão do navegador

        // Focar no campo de pesquisa
        if (searchInputRef.current) {
          // Tentar diferentes abordagens para focar
          const inputElement = searchInputRef.current;

          // 1. Focar diretamente
          inputElement.focus();

          // 2. Tentar encontrar o input real dentro do componente Material-UI
          setTimeout(() => {
            // Tentar focar no input real se existir
            const actualInput = inputElement.querySelector('input') || inputElement;
            if (actualInput) {
              actualInput.focus();

              // Selecionar texto
              setTimeout(() => {
                if (actualInput.select) {
                  actualInput.select();
                } else if (actualInput.setSelectionRange && actualInput.value) {
                  actualInput.setSelectionRange(0, actualInput.value.length);
                }
              }, 50);
            }
          }, 100);
        }
      }
    };

    // Adicionar listener no documento
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    searchInputRef,
    filteredItems,
    hasSearch: !!debouncedSearchTerm.trim(),
    totalResults: filteredItems.length,
    totalItems: items.length
  };
}; 